import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import styled from 'styled-components'
import { ButtonBase } from '@mui/material'
import { UIStyles, surfaces } from '../../theme'
import { StyledSwitchComponent } from '../UIComponents/StyledSwitch/StyledSwitch'
import { checkLayiNotificationsOnLoad, isLayiNotificationsSupported, refreshLayiNotificationsText } from '../../chatbot/layiNotifications'
import useLayiNotificationsSwitch from '../../chatbot/useLayiNotificationsSwitch'

// The per-user LAYI notifications check, and the invite card it may show, on every authenticated page (the chat
// page included). Once per page load and user it makes the shared opt-in key follow the user who is logged in,
// then decides whether to invite them (chatbot/layiNotifications.js has the rules). The card holds the same switch
// as the user menu row, so its click asks for the browser's permission, plus a sentence and a close button.

const TOP_BAR_HEIGHT = 60
const LAYI_PILL_HEIGHT = 64
// Below this window width the card (400px, centred) and the LAYI pill (top right) overlap.
const NARROW_WINDOW_BELOW = 960

// Floats under the top bar, centred, level with the LAYI pill's top, as a floating map panel: the panels' glass,
// an inset mint contour and 12px corners. z-index 9 like the LAYI widget, which comes later in the page, so the
// open chat covers the card; the top bar and its menus (10) and the modals stay above it. On narrow windows the
// centred card reaches the pill, which would cover its close button: there it moves below the pill (72px + the
// pill's 64px + 12px).
const Card = styled.div`
  position: fixed;
  top: ${TOP_BAR_HEIGHT + 12}px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  width: 400px;
  max-width: calc(100vw - 32px);
  border-radius: 12px;
  background: ${UIStyles.blueColorOpacity};
  -webkit-backdrop-filter: ${surfaces.glassBackdropFilter};
  backdrop-filter: ${surfaces.glassBackdropFilter};
  box-shadow: ${surfaces.mapPanelShadow};
  color: ${UIStyles.whiteColor};
  font-family: 'Roboto', sans-serif;
  @media (max-width: ${NARROW_WINDOW_BELOW - 1}px) {
    top: ${TOP_BAR_HEIGHT + 12 + LAYI_PILL_HEIGHT + 12}px;
  }
`

// Everything but the close button is the switch, with one click handler (the switch inside only draws the state).
const SwitchArea = styled(ButtonBase)`
  && {
    flex: 1 1 auto;
    min-width: 0;
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    grid-template-areas: 'icon label switch' '. sentence sentence';
    column-gap: 12px;
    align-items: center;
    justify-items: start;
    padding: 8px 0 14px 16px;
    border-radius: 12px 0 0 12px;
    color: inherit;
    font-family: inherit;
    text-align: left;
  }
  &&.Mui-focusVisible {
    outline: 2px solid ${UIStyles.mintColor};
    outline-offset: -2px;
  }
`

const Bell = styled.i`
  grid-area: icon;
  justify-self: center;
  font-size: 16px;
  color: ${UIStyles.mintColor};
`

const Label = styled.span`
  grid-area: label;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`

const SwitchSlot = styled.span`
  grid-area: switch;
`

const Sentence = styled.span`
  grid-area: sentence;
  margin-top: 2px;
  font-size: 13px;
  line-height: 18px;
  color: ${UIStyles.whiteTransparent08};
`

// A bare cross, like the close controls inside panels: soft white, red under the pointer.
const CloseButton = styled.button`
  flex: none;
  width: 28px;
  height: 28px;
  margin: 10px 10px 0 4px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${UIStyles.transparent};
  color: ${UIStyles.whiteTransparent08};
  font-size: 15px;
  line-height: 28px;
  cursor: pointer;
  transition: color 0.15s;
  &:hover {
    color: ${UIStyles.redColor};
  }
  &:focus-visible {
    outline: 2px solid ${UIStyles.mintColor};
    outline-offset: 0;
  }
`

const InviteCard = ({ t, userId, onClose, openLayiNotificationsModal }) => {
  const labelId = useId()
  const sentenceId = useId()
  const { on, toggle } = useLayiNotificationsSwitch({
    t,
    userId,
    source: 'invite',
    onNeedsModal: ({ blocked }) => {
      onClose()
      openLayiNotificationsModal({ blocked })
    }
  })

  // Turned on here, in the user menu or in another tab: the invite has done its job.
  useEffect(() => {
    if (on) onClose()
  }, [on, onClose])

  const closeOnEscape = event => {
    if (event.key === 'Escape') onClose()
  }

  return (
    <Card role="region" aria-labelledby={labelId} data-testid="layi-notifications-invite" onKeyDown={closeOnEscape}>
      <SwitchArea component="div" role="switch" aria-checked={on} aria-labelledby={labelId} aria-describedby={sentenceId} onClick={toggle}>
        <Bell className="fa-regular fa-bell" aria-hidden="true" />
        <Label id={labelId}>{t('layi_notifications_label')}</Label>
        <SwitchSlot>
          <StyledSwitchComponent size="small" checked={on} inputProps={{ style: { display: 'none' } }} />
        </SwitchSlot>
        <Sentence id={sentenceId}>{t('layi_notifications_invite')}</Sentence>
      </SwitchArea>
      <CloseButton type="button" aria-label={t('Close')} onClick={onClose}>
        <i className="fas fa-times" aria-hidden="true" />
      </CloseButton>
    </Card>
  )
}

export default function LayiNotificationsInvite({ canUseLayi, userId, hasRoleIrriga, t, openLayiNotificationsModal }) {
  const [visible, setVisible] = useState(false)
  const tRef = useRef(t)
  tRef.current = t
  // The user menu is the only place to turn them off again, and ROLE_IRRIGA users have no user menu.
  const canInvite = Boolean(canUseLayi) && !hasRoleIrriga
  const close = useCallback(() => setVisible(false), [])
  // The strings, not t: t is a new function on every store update.
  const title = t('layi_notification_title')
  const body = t('layi_notification_body')

  // Once per page load and user, and again when another user logs in without a reload.
  useEffect(() => {
    const translate = tRef.current
    const text = { title: translate('layi_notification_title'), body: translate('layi_notification_body') }
    setVisible(checkLayiNotificationsOnLoad({ userId, canInvite, text }))
  }, [userId, canInvite])

  // After the check: a language change rewrites the stored text while they are on, also with no chat frame
  // mounted, so LAYI's pop-out and /layi tabs notify in the app's language.
  useEffect(() => {
    refreshLayiNotificationsText({ title, body })
  }, [title, body])

  // Nothing of LAYI shows without ROLE_WEB_LAYI, nor on browsers that cannot notify.
  if (!visible || !canUseLayi || !isLayiNotificationsSupported()) return null
  return <InviteCard t={t} userId={userId} onClose={close} openLayiNotificationsModal={openLayiNotificationsModal} />
}
